/**
 * Page Anchor Nav — portable core (kit v1.2)
 * - Smooth scroll to [data-anchor-target] ids
 * - Duplicate landing ids: first in document order wins (later nodes lose id)
 * - Optional pin bar under header (teleport track to document.body)
 * - When pinned: CSS hides icons, text bar remains
 * - Active state by document position (not button order)
 *
 * Theme adapter:
 *   data-extra-offset    — px below header
 *   data-enable-sticky   — "true" | "false" (sticky on by default)
 * Header offset uses .header-section (Horizon), then CSS --header-height fallback.
 */
if (!customElements.get('page-anchor-nav')) {
  class PageAnchorNav extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.querySelector('.page-anchor-nav__wrapper');
      this.track = this.querySelector('.page-anchor-nav__track');
      this.placeholder = this.querySelector('.page-anchor-nav__placeholder');
      this.buttons = Array.from(this.querySelectorAll('[data-anchor-target]'));
      this.extraTop = parseInt(this.dataset.extraOffset || '0', 10) || 0;
      this.enableSticky = this.dataset.enableSticky !== 'false';
      this.smoothScroll = this.dataset.smooth !== 'false';
      this.headerSelector = '.header-section, header, [data-header], #shopify-section-header';
      this.pinned = false;
      this._unpinScrollY = 0;
      this._activeLock = false;
      this._unlockTimer = null;
      this._trackHomeParent = null;
      this._trackHomeNext = null;

      /* Duplicate Custom Section Id: keep first id in document order, strip later ones */
      this._dedupeLandingIds = () => {
        const seen = new Set();
        document.querySelectorAll('.custom-anchor-target[id]').forEach((el) => {
          const id = el.getAttribute('id');
          if (!id) return;
          if (seen.has(id)) {
            el.removeAttribute('id');
            el.setAttribute('data-anchor-landing-dup', id);
          } else {
            seen.add(id);
          }
        });
      };

      this._getTargetEl = (id) => {
        if (!id) return null;
        return document.getElementById(id);
      };

      this._readPinTop = () => {
        const headerEl = document.querySelector(this.headerSelector);
        if (headerEl) {
          const bottom = headerEl.getBoundingClientRect().bottom;
          return Math.max(0, Math.round(bottom) + this.extraTop);
        }
        const root = document.documentElement;
        const h = parseInt(getComputedStyle(root).getPropertyValue('--header-height'), 10) || 0;
        const ann =
          parseInt(getComputedStyle(root).getPropertyValue('--announcement-height'), 10) || 0;
        return Math.max(0, h + ann + this.extraTop);
      };

      this._applyPinTopVar = (pinTop) => {
        const value = `${pinTop}px`;
        this.style.setProperty('--anchor-nav-pin-top', value);
        if (this.track) {
          this.track.style.setProperty('--anchor-nav-pin-top', value);
          if (this.pinned) {
            this.track.style.top = value;
          }
        }
      };

      this._measureWrapperDocTop = () => {
        if (!this.wrapper) return 0;
        const r = this.wrapper.getBoundingClientRect();
        return r.top + window.scrollY;
      };

      this._syncLayout = () => {
        if (!this.wrapper || !this.track) return;
        const pinTop = this._readPinTop();
        this._applyPinTopVar(pinTop);
        if (!this.pinned) {
          this._unpinScrollY = this._measureWrapperDocTop() - pinTop;
        }
        if (this.pinned && this.placeholder) {
          this.placeholder.style.height = `${this.track.offsetHeight}px`;
        }
      };

      this._setPinned = (next) => {
        if (!this.enableSticky && next) return;
        if (next === this.pinned || !this.track || !this.placeholder) return;
        this.pinned = next;
        const pinTop = this._readPinTop();

        if (next) {
          /* Escape clip-path / transform containing blocks on main content */
          this._trackHomeParent = this.track.parentNode;
          this._trackHomeNext = this.track.nextSibling;
          this.placeholder.style.height = `${this.track.offsetHeight}px`;
          document.body.appendChild(this.track);
          this.track.classList.add('is-pinned');
          this._applyPinTopVar(pinTop);
          /* Sync placeholder height after icon collapse */
          requestAnimationFrame(() => {
            if (this.pinned && this.placeholder) {
              this.placeholder.style.height = `${this.track.offsetHeight}px`;
            }
          });
        } else {
          this.track.classList.remove('is-pinned');
          this.track.style.top = '';
          const parent = this._trackHomeParent;
          const nextNode = this._trackHomeNext;
          if (parent) {
            if (nextNode && nextNode.parentNode === parent) {
              parent.insertBefore(this.track, nextNode);
            } else {
              parent.appendChild(this.track);
            }
          }
          this._trackHomeParent = null;
          this._trackHomeNext = null;
          this.placeholder.style.height = '';
          requestAnimationFrame(() => this._syncLayout());
        }
      };

      this._setActiveButton = (activeBtn) => {
        this.buttons.forEach((b) => b.classList.toggle('is-active', b === activeBtn));
      };

      this._clearActiveLock = () => {
        if (this._unlockTimer) {
          window.clearTimeout(this._unlockTimer);
          this._unlockTimer = null;
        }
        if (this._onScrollEndBound) {
          window.removeEventListener('scrollend', this._onScrollEndBound);
          this._onScrollEndBound = null;
        }
        this._activeLock = false;
      };

      this._lockActiveUntilScrollSettles = (btn) => {
        this._clearActiveLock();
        this._activeLock = true;
        this._setActiveButton(btn);

        const unlock = () => {
          this._clearActiveLock();
          this._updateActiveFromScroll();
        };

        this._onScrollEndBound = unlock;
        window.addEventListener('scrollend', this._onScrollEndBound, { once: true });
        this._unlockTimer = window.setTimeout(unlock, 900);
      };

      this._onScroll = () => {
        if (!this.wrapper || !this.track) return;
        const pinTop = this._readPinTop();
        this._applyPinTopVar(pinTop);

        if (this.enableSticky) {
          const scrollY = window.scrollY || window.pageYOffset;

          if (!this.pinned) {
            this._unpinScrollY = this._measureWrapperDocTop() - pinTop;
            const top = this.wrapper.getBoundingClientRect().top;
            if (top <= pinTop + 0.5) {
              this._setPinned(true);
            }
          } else if (scrollY < this._unpinScrollY - 1) {
            this._setPinned(false);
          }
        } else if (this.pinned) {
          this._setPinned(false);
        }

        this._updateActiveFromScroll();
      };

      this._onResize = () => {
        this._syncLayout();
        this._onScroll();
      };

      this._parseTargetId = (raw) => {
        // Strip leading # / . (common when pasting selectors); reject whitespace ids
        const id = (raw || '').replace(/^[#.]+/, '').trim();
        if (!id || /\s/.test(id)) return '';
        return id;
      };

      this._onClick = (e) => {
        const btn = e.target.closest('[data-anchor-target]');
        if (!btn || !this.track || !this.track.contains(btn)) return;
        e.preventDefault();
        const id = this._parseTargetId(btn.getAttribute('data-anchor-target'));
        if (!id) return;
        this._dedupeLandingIds();
        const el = this._getTargetEl(id);
        if (!el) {
          if (window.Shopify && Shopify.designMode) {
            console.warn('[page-anchor-nav] target not found id=', id);
          }
          return;
        }

        const pinTop = this.enableSticky ? this._readPinTop() : 0;
        this._applyPinTopVar(pinTop);
        const navH = this.track ? this.track.offsetHeight : 0;
        const scrollMargin = parseInt(getComputedStyle(el).scrollMarginTop, 10) || 0;
        const pad = 8;
        /* When sticky: estimate pinned text-bar height so icon height does not inflate offset */
        let effectiveNavH = navH;
        if (this.enableSticky && !this.pinned) {
          const media = this.track.querySelector('.page-anchor-nav__media');
          if (media) {
            effectiveNavH = Math.max(0, navH - media.offsetHeight);
          }
        }
        const top =
          el.getBoundingClientRect().top +
          window.scrollY -
          pinTop -
          effectiveNavH -
          scrollMargin -
          pad;
        window.scrollTo({
          top: Math.max(0, top),
          behavior: this.smoothScroll ? 'smooth' : 'auto',
        });

        this._lockActiveUntilScrollSettles(btn);

        if (!el.hasAttribute('tabindex')) {
          el.setAttribute('tabindex', '-1');
        }
        window.setTimeout(() => {
          try {
            el.focus({ preventScroll: true });
          } catch (err) {
            /* ignore */
          }
        }, 450);
      };

      this._updateActiveFromScroll = () => {
        if (this._activeLock || !this.buttons.length) return;
        const pinTop = this.enableSticky ? this._readPinTop() : 0;
        const navH = this.track ? this.track.offsetHeight : 0;
        const marker = window.scrollY + pinTop + navH + 12;
        let active = null;
        let bestTop = -Infinity;
        this.buttons.forEach((btn) => {
          const tid = this._parseTargetId(btn.getAttribute('data-anchor-target'));
          const el = tid && this._getTargetEl(tid);
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY;
          if (top <= marker && top >= bestTop) {
            bestTop = top;
            active = btn;
          }
        });
        this._setActiveButton(active);
      };

      this._onScrollBound = this._onScroll.bind(this);
      this._onResizeBound = this._onResize.bind(this);
      this._onClickBound = this._onClick.bind(this);

      requestAnimationFrame(() => {
        this._dedupeLandingIds();
        this._syncLayout();
        this._onScroll();
      });

      window.addEventListener('scroll', this._onScrollBound, { passive: true });
      window.addEventListener('resize', this._onResizeBound);
      if (this.track) {
        this.track.addEventListener('click', this._onClickBound);
      }
    }

    disconnectedCallback() {
      this._clearActiveLock();
      if (this.pinned) {
        this._setPinned(false);
      }
      window.removeEventListener('scroll', this._onScrollBound);
      window.removeEventListener('resize', this._onResizeBound);
      if (this.track) {
        this.track.removeEventListener('click', this._onClickBound);
      }
    }
  }

  customElements.define('page-anchor-nav', PageAnchorNav);
}
